import React, { useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";
import { formatCurrencyBDT } from "../../utils/currency";

interface ConfirmUntaggedPaymentDialogProps {
    open: boolean;
    amount: number;
    /** Matches the parent dialog's own save-button wording ("Add Payment",
     * "Update Payment", "Record Payment", …) so this reads as a natural
     * continuation of that action, not a generic "Yes". */
    confirmLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * A one-question checkpoint shown when a payment is about to be saved with
 * no cash/bank account selected — the account picker is easy to skip past
 * without noticing, and an untagged payment silently never counts toward
 * any account's balance (which is otherwise always computed live from
 * exactly these tags, see FinancialAccountService). Fires on every
 * no-account save by design, so it catches the moment someone forgot
 * rather than just the first time. "Go Back" is the emphasized default —
 * the point of this dialog is to make skipping the tag a deliberate choice,
 * not an accident.
 */
export const ConfirmUntaggedPaymentDialog: React.FC<ConfirmUntaggedPaymentDialogProps> = ({
    open,
    amount,
    confirmLabel,
    onConfirm,
    onCancel,
}) => {
    // `autoFocus` on the button loses to the dialog's own focus handling, so
    // focus "Go Back" once the dialog is in — then Enter means Go Back.
    const goBackRef = useRef<HTMLButtonElement>(null);
    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="xs"
            fullWidth
            slotProps={{ transition: { onEntered: () => goBackRef.current?.focus() } }}
        >
            <DialogTitle>No account selected</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <WarningAmberRounded color="warning" sx={{ mt: 0.25, flexShrink: 0 }} />
                    <Typography variant="body2">
                        {formatCurrencyBDT(amount)} won't be tied to any cash/bank account, so it
                        won't count toward any account's balance. Go back and pick one, or continue
                        if that's intentional.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onConfirm}>{confirmLabel}</Button>
                <Button ref={goBackRef} onClick={onCancel} variant="contained" autoFocus>
                    Go Back
                </Button>
            </DialogActions>
        </Dialog>
    );
};
